import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Form, Input, Row } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2becAddressForm from '@/components/B2becAddressForm'
import B2becTranslation from '@/components/B2becTranslation'
import CustomButton from '@/components/CustomButton'
import { useAppSelector } from '@/store/hook'
import { selectCountryNames } from '@/store/selectors/configSelector'
import {
  selectCustomerAddress,
  selectIsFetchingCustomerAddress,
} from '@/store/slices/customerSlice'
import { isFormDirty } from '@/libs/utils/validatorUtils'

const { useForm } = Form

const { TextArea } = Input

const validateMessages = {
  required: (label) => {
    return (
      <B2becTranslation
        isComponent={false}
        value="form.validate.required"
        variables={{ label }}
      />
    )
  },
}

const InvoiceChangeForm = (props) => {
  const { disabled = false, onFinish, isSubmiting = false } = props
  const [form] = useForm()
  const { t } = useTranslation()

  const customerAddress = useAppSelector(selectCustomerAddress)
  const isCustomerLoading = useAppSelector(selectIsFetchingCustomerAddress)
  const countryNamesConfig = useAppSelector(selectCountryNames)

  const countryName = useMemo(() => {
    if (!customerAddress) {
      return ''
    }

    const foundConfig = countryNamesConfig.find(
      (config) => config.value === customerAddress.country,
    )

    return foundConfig ? foundConfig.name : customerAddress.country
  }, [countryNamesConfig, customerAddress])

  const handleRequestInvoiceChange = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue(false)

      onFinish({ ...values, country: customerAddress.country })
    } catch {
      // TODO handle exception
    }
  }

  const renderSkeleton = () => {
    return (
      <div className="b2bec-change-invoice-skeleton">
        <Form.Item
          className="custom-form-item"
          label={t('form.label.companyName')}
        >
          <Skeleton style={{ height: 46 }} />
        </Form.Item>

        <Form.Item
          className="custom-form-item"
          label={t('form.label.extraText')}
        >
          <Skeleton style={{ height: 66 }} />
        </Form.Item>

        <Row gutter={24}>
          <Col span={16}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.street')}
            >
              <Skeleton style={{ height: 46 }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.houseNumber')}
            >
              <Skeleton style={{ height: 46 }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item className="custom-form-item" label={t('form.label.zip')}>
              <Skeleton style={{ height: 46 }} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.city')}
            >
              <Skeleton style={{ height: 46 }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          className="custom-form-item"
          label={t('form.label.country')}
          style={{ width: 320 }}
        >
          <Skeleton style={{ height: 46 }} />
        </Form.Item>

        <Form.Item
          className="custom-form-item"
          label={t('form.label.annotation')}
        >
          <Skeleton style={{ height: 166 }} />
        </Form.Item>
      </div>
    )
  }

  const renderForm = useCallback(() => {
    return (
      <>
        <B2becAddressForm />
        <Form.Item
          className="custom-form-item"
          disabled={disabled}
          label={t('form.label.annotation')}
          name="annotation"
        >
          <TextArea disabled={disabled} rows={7} style={{ resize: 'none' }} />
        </Form.Item>

        <Form.Item noStyle name="partnerNumber">
          <Input type="hidden" />
        </Form.Item>

        <Form.Item name="phoneNumber" noStyle>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item name="phoneNumber" noStyle>
          <Input type="hidden" />
        </Form.Item>
      </>
    )
  }, [disabled, t])

  return (
    <div className="b2bec-change-invoice__form">
      <Form
        layout="vertical"
        form={form}
        key={customerAddress?.partnerNumber}
        requiredMark={false}
        validateMessages={validateMessages}
        initialValues={{
          ...customerAddress,
          country: countryName,
        }}
      >
        {isCustomerLoading ? renderSkeleton() : renderForm()}
        <div className="b2bec-change-invoice__form__footer">
          <Form.Item noStyle shouldUpdate>
            {({ getFieldsValue }) => {
              const values = getFieldsValue(false)
              const isDirty = isFormDirty(customerAddress, values)
              const isDisabled = !isDirty || isCustomerLoading || disabled || isSubmiting

              return (
                <CustomButton
                  disabled={isDisabled}
                  loading={isSubmiting}
                  onClick={handleRequestInvoiceChange}
                >
                  {t('changeInvoiceAddress.button.requestChanges')}
                </CustomButton>
              )
            }}
          </Form.Item>
        </div>
      </Form>
    </div>
  )
}

export default memo(InvoiceChangeForm)
